import { Component, EventEmitter, Output, ViewChild, ElementRef, AfterViewChecked, HostListener, OnDestroy } from '@angular/core';
import { CommonService } from './../../../services/common/common.service';
import { Subscription } from 'rxjs';
import { LolLoggerService } from '../../../common/shared/lol-logger.service';
import { LocalStorageService } from './../../../services/infrastructure/local-storage.service';
import { MasterConstants } from './../../../common/constants/master-constants';
import { getTrackingValidation, isValidTrackingMode, TRACKING_PLACEHOLDERS, ShipmentValidationResult } from './shipment-identifier-validator';

export interface ChatMessage {
  text?: string;
  isUser: boolean;
  timestamp: Date;
  actions?: ChatMenuItem[];
  type?: 'menu' | 'tracking-menu' | 'normal' | 'error';
  trackingData?: {
    accountNumber?: string;
    shipmentNumber?: string;
    orderNumber?: string;
    referenceValue?: string;
    status?: string;
    trackingUrl?: string;
    estimatedDelivery?: string;
    trackingType?: string;
    // Multiple shipments array
    shipments?: Array<{
      shipmentId: string;
      status: string;
      orders?: string[];
      trackingUrl?: string;
    }>;
  };
  // Shipment list state
  visibleShipmentCount?: number;
  expandedShipmentId?: string | null;
}
export interface ChatMenuItem {
  key: string;
  label: string;
  prompt?: string;
  trackingMode?: string;
}

export interface ChatMenuConfig {
  primaryMenu: ChatMenuItem[];
  secondaryMenu: { [primaryKey: string]: ChatMenuItem[] };
}


@Component({
  selector: 'app-ai-chatbot',
  templateUrl: './ai-chatbot.component.html',
  styleUrls: ['./ai-chatbot.component.scss']
})
export class AiChatbotComponent implements AfterViewChecked, OnDestroy {
  @Output() closeChatbot = new EventEmitter<void>();
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('liveMessagesContainer') private liveMessagesContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;

  readonly liveAgent = { name: ' ', role: '', shortName: '', connectedtimestamp: new Date() };


  chatMode: 'ai' | 'live' = 'ai';
  messages: ChatMessage[] = [];
  liveUserMessages: { text: string; timestamp: Date | string; role: string }[] = [];
  userInput: string = '';
  isTyping: boolean = false;
  subscription = new Subscription();
  currentTrackingMode: string = '';
  isTrackingFlowActive = false;
  activeTrackingMode = '';
  activePrimaryMenuKey = '';
  isKebabMenuOpen = false;
  isAwaitingOptionSelection = false;
  selectedTrackingDetail = false;
  isLiveAgentOnline = false;

  // Live agent availability
  isLiveAgentServiceAvailable = true;
  liveAgentOfflineMessage = "We're currently offline. Please email winfieldcustomerservice@landolakes.com or" +
    " call 1-855 - 494 - 6343 between 7:00 AM and 5:00 PM CST, Monday through Friday." +
    " We are closed on weekends and company holidays.We'll get back to you as soon as possible during normal business hours";
  // Set once the connecting delay has elapsed, so the offline layout is not shown instantly.
  isOfflineStateVisible = false;
  private offlineStateTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // Pending end-conversation prompt: 'back' returns to the assistant, 'close' minimizes the chat.
  pendingConfirm: 'back' | 'close' | null = null;
  isMinimizing = false;
  isCppFlowActive = false;
  userName = '';
  welcomeGreeting = '';
  conversationId = '';
  processedIds = new Set<string>();
  private lastScrollKey = '';
  secondaryMenuMessage = 'Got it! What info do you have? Pick one below.';
  virtualAssitant = 'Evolve';
  menuConfig: ChatMenuConfig = {
    primaryMenu: [],
    secondaryMenu: {}
  };
  userMenuAccessList: any;
  headerCompany = MasterConstants.US_COMPANYCODE;

  // Shipments shown per page
  private readonly DEFAULT_BATCH = 6;

  // Statuses unknown, unsupported shown with a message.
  private readonly SUPPORTED_STATUSES = ['DELIVERED', 'ORDER IN PROGRESS', 'SHIPPED IN TRANSIT'];

  private isSupportedStatus(status?: string): boolean {
    return this.SUPPORTED_STATUSES.includes((status || '').trim().toUpperCase());
  }

  // Template-facing check for a single shipment row's status.
  isShipmentStatusSupported(status?: string): boolean {
    return this.isSupportedStatus(status);
  }

  // True when the API returned a valid shipment but no usable tracking status.
  isTrackingUnavailable(trackingData: ChatMessage['trackingData']): boolean {
    if (!trackingData || trackingData.shipments?.length) {
      return false;
    }
    return !this.isSupportedStatus(trackingData.status);
  }

  // Slice visible shipments
  visibleShipments(message: ChatMessage) {
    const shipments = message.trackingData?.shipments || [];
    const displayedCount = message.visibleShipmentCount || this.DEFAULT_BATCH;
    return shipments.slice(0, displayedCount);
  }

  // Toggle row expansion
  toggleShipmentExpansion(message: ChatMessage, shipmentId: string) {
    const expanded = message.expandedShipmentId;
    message.expandedShipmentId = expanded === shipmentId ? null : shipmentId;
  }

  // Check row expanded
  isShipmentExpanded(message: ChatMessage, shipmentId: string): boolean {
    return message.expandedShipmentId === shipmentId;
  }

  // Load next batch
  loadMoreShipments(message: ChatMessage) {
    const current = message.visibleShipmentCount || this.DEFAULT_BATCH;
    message.visibleShipmentCount = current + this.DEFAULT_BATCH;
  }

  // Group API rows into shipments, collecting the order numbers of each shipment
  private toShipments(records: any[]): NonNullable<NonNullable<ChatMessage['trackingData']>['shipments']> {
    const shipments = new Map<string, { shipmentId: string; status: string; orders: string[]; trackingUrl?: string }>();

    records.forEach(record => {
      const shipmentId = (record?.shipmentNumber ?? record?.ShipmentNumber ?? '').toString();
      const orderNumber = (record?.orderNumber ?? record?.OrderNumber ?? '').toString();
      const shipment = shipments.get(shipmentId);

      if (shipment) {
        if (orderNumber && !shipment.orders.includes(orderNumber)) {
          shipment.orders.push(orderNumber);
        }
        return;
      }

      shipments.set(shipmentId, {
        shipmentId,
        status: record?.status ?? record?.Status,
        orders: orderNumber ? [orderNumber] : [],
        trackingUrl: record?.trackingUrl ?? record?.TrackingUrl
      });
    });

    return Array.from(shipments.values());
  }

  // Count shipments by status
  getStatusBreakdown(message: ChatMessage): string[] {
    const shipments = message.trackingData?.shipments || [];
    const statusCounts: Record<string, number> = {};
    shipments.forEach(shipment => {
      // Unsupported statuses are never surfaced, so they are excluded from the breakdown.
      if (!this.isSupportedStatus(shipment.status)) {
        return;
      }
      const label = this.toTitleCase(shipment.status || '');
      statusCounts[label] = (statusCounts[label] || 0) + 1;
    });
    return Object.keys(statusCounts).map(label => `${statusCounts[label]} ${label}`);
  }

  // Word-by-word capitalize
  private toTitleCase(value: string): string {
    return value.trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  }

  // Reuses existing status mapping
  statusClass(status?: string): string {
    if (!this.isSupportedStatus(status)) return 'status--neutral';
    const display = this.getTrackingDisplay(status);
    if (display.isDelivered) return 'status--delivered';
    if (display.label === 'IN-TRANSIT') return 'status--in-transit';
    return 'status--neutral';
  }

  private readonly AI_CHAT_MENU_CONFIG_KEY = 'AI_CHAT_MENU_CONFIG';
  private readonly CONVERSATION_ID = 'CONVERSATION_ID';
  private chatStatusIntervalId: ReturnType<typeof setInterval> | null = null;
  private chatStatusSubscription: Subscription | null = null;

  readonly iconBase = 'assets/icon/ai-chatbot-icons';
  private readonly actionIconMap: Record<string, string> = {
    'track-shipment': `${this.iconBase}/truck.svg`,
    'create-request': `${this.iconBase}/main_menu.svg`,
    'create-order': `${this.iconBase}/cpp_order.png`,
    'create-cpp-order': `${this.iconBase}/cpp_order.png`,
    'find-inventory': `${this.iconBase}/web_order_number.svg`,
    'track-by-bol': `${this.iconBase}/truck.svg`,
    'track-by-shipment-number': `${this.iconBase}/truck.svg`,
    'track-by-order-number': `${this.iconBase}/cpp_order.png`,
    'track-by-customer-po': `${this.iconBase}/cpp_order.png`,
    'track-by-web-order-number': `${this.iconBase}/web_order_number.svg`
  };
  private readonly actionLabelMap: Record<string, string> = {
    'create-order': 'Order Crop Protection Products',
    'create-cpp-order': 'Create a CPP Order'
  };
  private readonly actionSubtitleMap: Record<string, string> = {
    'track-shipment': 'Track orders & shipments',
    'create-request': 'Create & manage orders',
    'create-order': 'Search products & place orders',
    'customer-service': 'Create a CPP Order',
    'create-cpp-order': 'Create a CPP Order',
    'find-inventory': 'Create a CPP Order',
    'track-by-bol': 'Bill of Lading',
    'track-by-shipment-number': 'Shipment ID',
    'track-by-order-number': 'Sales order #',
    'track-by-customer-po': 'Purchase Order #',
    'track-by-web-order-number': 'Online order #'
  };

  constructor(private commonService: CommonService,
    private localStorageService: LocalStorageService) {
    this.loadUserName();
    this.initializeChat();
    this.loadMenuConfig();
  }

  private loadUserName(): void {
    try {
      const evolveAccess = localStorage.getItem('evolveAccess');
      if (evolveAccess) {
        const userData = JSON.parse(evolveAccess).resultData[0];
        const capitalize = (str: string) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
        this.userName = `${capitalize(userData.firstName)} ${capitalize(userData.lastName)}`.trim();
      }
    } catch (e) {
      this.userName = '';
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isKebabMenuOpen = false;
    this.pendingConfirm = null;
  }

  ngAfterViewChecked(): void {
    const scrollKey = `${this.chatMode}|${this.messages.length}|${this.liveUserMessages.length}|${this.isTyping}|${this.isLiveAgentOnline}`;
    if (scrollKey === this.lastScrollKey) {
      return;
    }
    this.lastScrollKey = scrollKey;
    this.scrollToBottom();
  }

  initializeChat(): void {
    this.currentTrackingMode = "";
    this.welcomeGreeting = `Hey ${this.userName}! What can I help you with today?`;
    this.messages = [
      {
        text: this.welcomeGreeting,
        isUser: false,
        timestamp: new Date(),
        type: 'menu',
        actions: this.menuConfig.primaryMenu
      }
    ];

  }
  private isValidMenuConfig(menu: ChatMenuConfig): boolean {
    return !!menu
      && Array.isArray(menu.primaryMenu)
      && !!menu.secondaryMenu
      && typeof menu.secondaryMenu === 'object';
  }

  private tryGetMenuFromStorage(): ChatMenuConfig | null {
    const cached = this.localStorageService.get(this.AI_CHAT_MENU_CONFIG_KEY);
    if (!cached) {
      return null;
    }

    try {
      const parsed = JSON.parse(cached) as ChatMenuConfig;
      return this.isValidMenuConfig(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private setMenuToStorage(menu: ChatMenuConfig): void {
    this.localStorageService.set(this.AI_CHAT_MENU_CONFIG_KEY, JSON.stringify(menu));
  }

  loadMenuConfig(): void {
    const data = JSON.parse(this.localStorageService.get(MasterConstants.EVOLVE_ACCESS));
    this.userMenuAccessList = data.resultData.find(x => x.userIdCompanyCode == this.headerCompany).userMenuAccessList;

    const hasShipmentTrackingAccess = this.commonService.checkUserRoleAccessType(MasterConstants.FUNCTION_CODE.AI_VIRTUAL_ASSISTANT_SHIPMENT_TRACKING, MasterConstants.USER_ACCESS.EDIT_FULL, this.userMenuAccessList);
    const hasCreateCppOrderAccess = this.commonService.checkUserRoleAccessType(MasterConstants.FUNCTION_CODE.AI_VIRTUAL_ASSISTANT_CREATE_CPP_ORDER, MasterConstants.USER_ACCESS.EDIT_FULL, this.userMenuAccessList);
    const hasCPPOAccess = this.commonService.checkUserRoleAccessType(MasterConstants.FUNCTION_CODE.CROP_PROTECTION_PRODUCT_ORDER, MasterConstants.USER_ACCESS.EDIT_FULL, this.userMenuAccessList);

    const requestData = { userMessage: 'Show Menu', requestType: 'Menu' };

    this.subscription.add(this.commonService.getAIResponse(requestData).subscribe((result: any) => {
      const responseWrapper: any = result.message;
      const menu = responseWrapper?.responseData as ChatMenuConfig;

      if (this.isValidMenuConfig(menu)) {
        menu.primaryMenu = menu.primaryMenu.filter(item =>
          (item.key !== 'track-shipment' || hasShipmentTrackingAccess) &&
          ((item.key !== 'create-order' && item.key !== 'create-cpp-order') || (hasCreateCppOrderAccess && hasCPPOAccess)));
        this.menuConfig = menu;
      }

      this.initializeChat();
    }, error => LolLoggerService.error(error)));
  }

  checkChatStatus(): void {
    if (this.chatStatusIntervalId) {
      return;
    }

    this.chatStatusIntervalId = setInterval(() => {
      const convID = this.conversationId;
      if (!convID || this.chatMode !== 'live') {
        return;
      }

      if (this.chatStatusSubscription && !this.chatStatusSubscription.closed) {
        return;
      }

      this.chatStatusSubscription = this.commonService.recentConversation(convID).subscribe({
        next: (result: any) => {
          const responseWrapper: any = result;
          const message = responseWrapper?.message;
          if (!message) {
            return;
          }

          this.liveAgent.name = message.agentName ?? this.liveAgent.name;
          this.liveAgent.role = message.agentRole ?? this.liveAgent.role;
          this.liveAgent.shortName = message.agentName ?? this.liveAgent.shortName;
          this.liveAgent.connectedtimestamp = message.connectedTimestamp ?? this.liveAgent.connectedtimestamp;
          this.isLiveAgentOnline = !!message.agentName;

          if (message.message && message.id && !this.processedIds.has(message.id) && message.role === 'Agent') {
            this.liveUserMessages.push({
              text: message.message.trim(),
              timestamp: message.timestamp,
              role: message.role
            });
            this.processedIds.add(message.id);
          }
        },
        error: (error) => LolLoggerService.error(error)
      });
    }, 3000);
  }

  private stopChatStatus(): void {
    if (this.offlineStateTimeoutId) {
      clearTimeout(this.offlineStateTimeoutId);
      this.offlineStateTimeoutId = null;
    }

    this.isOfflineStateVisible = false;

    if (this.chatStatusIntervalId) {
      clearInterval(this.chatStatusIntervalId);
      this.chatStatusIntervalId = null;
    }

    if (this.chatStatusSubscription) {
      this.chatStatusSubscription.unsubscribe();
      this.chatStatusSubscription = null;
    }
  }

  ngOnDestroy(): void {
    this.stopChatStatus();
    this.subscription.unsubscribe();
  }

  sendMessage(): void {
    if (!this.userInput.trim()) return;

    if (this.chatMode === 'live') {
      this.liveUserMessages.push({
        text: this.userInput.trim(),
        timestamp: new Date(),
        role: 'User'
      });

      // Call SalesForce
      const requestData = { ConversationId: this.conversationId, message: this.userInput.trim() };
      this.subscription.add(this.commonService.sendSFMessage(requestData).subscribe(() => { },
        error => LolLoggerService.error(error)));

      this.userInput = '';
      return;
    }

    // Add user message
    this.messages.push({
      text: this.userInput,
      isUser: true,
      timestamp: new Date()
    });

    let messageToSend = this.userInput.trim();

    this.userInput = '';
    this.isTyping = true;

    switch (this.currentTrackingMode) {

      case 'ordernumber':
        messageToSend = `ordernumber ${messageToSend}`;
        break;

      case 'shipmentnumber':
        messageToSend = `shipmentnumber ${messageToSend}`;
        break;

      case 'bol':
        messageToSend = `bol ${messageToSend}`;
        break;

      case 'customerpo':
        messageToSend = `customerpo ${messageToSend}`;
        break;

      case 'webordernumber':
        messageToSend = `webordernumber ${messageToSend}`;
        break;
    }

    this.askAI(messageToSend);
  }

  askAI(message: any): void {
    const requestData = { userMessage: message.trim(), requestType: 'Tracking' };
    const latestUserInput = (this.messages[this.messages.length - 1]?.text || '').trim();

    this.subscription.add(this.commonService.getAIResponse(requestData).subscribe((result: any) => {
      const responseWrapper: any = result;
      const botMessage = responseWrapper?.message?.aiMessage as string;

      if (botMessage?.startsWith('SHOW_PRIMARY_MENU')) {
        this.activePrimaryMenuKey = '';
        this.currentTrackingMode = '';
        this.isTrackingFlowActive = false;
        this.activeTrackingMode = '';

        this.messages.push({
          text: 'Choose an option below.',
          isUser: false,
          timestamp: new Date(),
          type: 'normal',
          actions: this.menuConfig.primaryMenu
        });

        this.isTyping = false;
        return;
      }

      if (botMessage?.startsWith('SHOW_SECONDARY_MENU')) {
        this.isTrackingFlowActive = false;
        this.activeTrackingMode = '';
        const menuKey = botMessage.split(':')[1];
        const secondaryActions = this.menuConfig.secondaryMenu[menuKey] || [];
        this.activePrimaryMenuKey = menuKey;
        this.currentTrackingMode = '';

        this.messages.push({
          text: this.secondaryMenuMessage,
          isUser: false,
          timestamp: new Date(),
          type: 'tracking-menu',
          actions: secondaryActions
        });

        this.isTyping = false;
        return;
      }

      const response = responseWrapper?.message;
      const responseData = response?.responseData;
      const records: any[] = Array.isArray(responseData) ? responseData : responseData ? [responseData] : [];
      const trackingRecords = records.filter(record => !this.isNoRecordMessage(record?.message ?? record?.Message));
      const shipments = this.toShipments(trackingRecords);
      const firstRecord = trackingRecords[0];
      const accountNum = firstRecord?.accountNumber ?? firstRecord?.AccountNumber;
      const orderNum = firstRecord?.orderNumber ?? firstRecord?.OrderNumber;
      const shipmentNum = firstRecord?.shipmentNumber ?? firstRecord?.ShipmentNumber;
      const apiTrackingType = firstRecord?.trackingType ?? firstRecord?.TrackingType;
      const apiMessage = (response?.userMessage || '').toString().trim();
      const botLevelMessage = (botMessage || '').toString().trim();
      const isNoRecordMessage = this.isNoRecordMessage(botLevelMessage);
      const selectedTrackingType = apiTrackingType ?? (this.actionSubtitleMap[this.activeTrackingMode] || 'tracking value');

      const hasTracking = (accountNum != null && accountNum > 0)
        || (orderNum != null && orderNum > 0)
        || !!shipmentNum;

      if (hasTracking) {
        this.selectedTrackingDetail = false;
        this.isTrackingFlowActive = false;
        this.activeTrackingMode = '';
        this.currentTrackingMode = '';
        this.isAwaitingOptionSelection = false;

        const trackingMessage: ChatMessage = {
          isUser: false,
          timestamp: new Date(),
          trackingData: {
            accountNumber: accountNum?.toString(),
            shipmentNumber: shipmentNum,
            orderNumber: orderNum?.toString(),
            referenceValue: latestUserInput,
            status: firstRecord?.status ?? firstRecord?.Status,
            trackingUrl: firstRecord?.trackingUrl ?? firstRecord?.TrackingUrl,
            trackingType: selectedTrackingType
          }
        };

        if (shipments.length > 1) {
          trackingMessage.trackingData!.shipments = shipments;
          trackingMessage.visibleShipmentCount = this.DEFAULT_BATCH;
          trackingMessage.expandedShipmentId = null;
        }

        this.messages.push(trackingMessage);
      }
      else if (response?.aiMessage && !isNoRecordMessage) {
        const isErrorResponse = (response.aiMessage as string).toLowerCase().includes('unable to process');
        if (isErrorResponse) {
          this.selectedTrackingDetail = false;
          this.isTrackingFlowActive = false;
          this.activeTrackingMode = '';
          this.currentTrackingMode = '';
          this.isAwaitingOptionSelection = true;
        }
        this.messages.push({
          text: response.aiMessage,
          isUser: false,
          timestamp: new Date(),
          ...(isErrorResponse ? { type: 'error' as const } : {})
        });
      } else {
        // No record found — show not-found UI
        this.selectedTrackingDetail = false;
        this.isTrackingFlowActive = false;
        this.activeTrackingMode = '';
        this.currentTrackingMode = '';
        this.isAwaitingOptionSelection = true;
        this.messages.push({
          isUser: false,
          timestamp: new Date(),
          trackingData: {
            referenceValue: latestUserInput,
            status: undefined,
            trackingType: selectedTrackingType
          }
        });
      }

      this.isTyping = false;

      if (this.isTrackingInputReady) {
        this.focusMessageInput();
      }
    }, error => {
      LolLoggerService.error(error);
      this.isTyping = false;
    }));
  }

  restartConversation(): void {
    this.isTrackingFlowActive = false;
    this.activeTrackingMode = '';
    this.activePrimaryMenuKey = '';
    this.isCppFlowActive = false;
    this.userInput = '';
    this.selectedTrackingDetail = false;
    this.isAwaitingOptionSelection = false;
    this.initializeChat();
  }

  onCppOrderCompleted(): void {
    this.restartConversation();
  }

  /** Closes the chat window but keeps the CPP conversation history when the CPP page takes over. */
  onCppCloseChatWindow(): void {
    this.close();
  }

  onCppNavigateToTracking(): void {
    this.isCppFlowActive = false;
    this.isTrackingFlowActive = false;
    this.activeTrackingMode = '';
    this.activePrimaryMenuKey = 'track-shipment';
    this.userInput = '';
    this.selectedTrackingDetail = false;

    this.initializeChat();

    this.messages.push({
      text: 'Track My Shipment',
      isUser: true,
      timestamp: new Date()
    });

    const secondaryActions = this.menuConfig.secondaryMenu['track-shipment'];
    if (secondaryActions?.length) {
      this.messages.push({
        text: this.secondaryMenuMessage,
        isUser: false,
        timestamp: new Date(),
        type: 'tracking-menu',
        actions: secondaryActions
      });
    }
  }

  close(): void {
    this.pendingConfirm = null;
    this.closeChatbot.emit();
  }

  toggleConfirm(event: Event, action: 'back' | 'close'): void {
    event.stopPropagation();
    this.isKebabMenuOpen = false;
    if (!this.isLiveAgentServiceAvailable) {
      this.pendingConfirm = action;
      this.confirmEndConversation();
      return;
    }
    this.pendingConfirm = this.pendingConfirm === action ? null : action;
  }

  cancelConfirm(): void {
    this.pendingConfirm = null;
  }

  confirmEndConversation(): void {
    const action = this.pendingConfirm;
    this.pendingConfirm = null;
    this.endLiveConversation();

    if (action === 'close') {
      this.close();
    }
  }

  // Ends the conversation and clears live state so the next open starts in AI mode
  private endLiveConversation(): void {
    if (this.conversationId) {
      this.subscription.add(this.commonService.closeConversation(this.conversationId).subscribe(
        () => { },
        error => LolLoggerService.error(error)));
    }

    this.stopChatStatus();
    this.chatMode = 'ai';
    this.isLiveAgentOnline = false;
    this.conversationId = '';
    this.liveUserMessages = [];
    this.processedIds.clear();
  }

  toggleKebabMenu(event: Event): void {
    event.stopPropagation();
    this.isKebabMenuOpen = !this.isKebabMenuOpen;
  }

  emailSFTranscript(): void {
    this.subscription.add(this.commonService.sendSFTranscript(this.conversationId).subscribe(() => { },
      error => LolLoggerService.error(error)));
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.isSendDisabled) {
        this.sendMessage();
      }
    }
  }

  private scrollToBottom(): void {
    try {
      const container = this.chatMode === 'live' ? this.liveMessagesContainer : this.messagesContainer;
      if (container) {
        container.nativeElement.scrollTop = container.nativeElement.scrollHeight;
      }
    } catch (err) { }
  }

  connectToLiveAgent(): void {
    this.chatMode = 'live';
    this.liveUserMessages = [];
    this.isLiveAgentServiceAvailable = true;

    this.checkChatStatus();

    if (this.conversationId == '') {
      this.subscription.add(this.commonService.createConversation().subscribe((result: any) => {
        const responseWrapper: any = result;
        if (!responseWrapper?.message) {
          this.isLiveAgentServiceAvailable = false;
          this.offlineStateTimeoutId = setTimeout(() => this.isOfflineStateVisible = true, 2500);
          return;
        }
        this.conversationId = responseWrapper?.message;

      }, error => LolLoggerService.error(error)));
    }
  }

  backToEvolveAgent(): void {
    this.chatMode = 'ai';
    this.pendingConfirm = null;
    this.stopChatStatus();
  }

  handleQuickAction(action: ChatMenuItem): void {
    const validateEvolveData = JSON.parse(this.localStorageService.get(MasterConstants.EVOLVE_ACCESS));
    const isAccountSet = validateEvolveData.resultData[0].isSetAccount;
    if (isAccountSet === false) {
      this.messages.push({
        text: "Please select an account before you proceed.",
        isUser: false,
        timestamp: new Date()
      });

      return;
    }
    if (!action) {
      return;
    }

    if (action.key === 'Find Inventory') {
      return;
    }

    if (action.key === 'create-order') {
      this.messages.push({ text: this.getActionLabel(action), isUser: true, timestamp: new Date() });
      this.activePrimaryMenuKey = action.key;
      this.isCppFlowActive = true;
      return;
    }

    this.messages.push({
      text: this.getActionLabel(action),
      isUser: true,
      timestamp: new Date()
    });



    const secondaryActions = this.menuConfig.secondaryMenu[action.key];

    if (secondaryActions?.length) {
      this.activePrimaryMenuKey = action.key;

      this.messages.push({
        text: this.secondaryMenuMessage,
        isUser: false,
        timestamp: new Date(),
        type: 'tracking-menu',
        actions: secondaryActions
      });

      return;
    }

    if (action.trackingMode && action.prompt) {
      this.currentTrackingMode = action.trackingMode;
      this.startTrackingFlow(action.key);
      this.askAI(action.prompt);
    }
  }

  startTrackingFlow(action: string): void {
    this.isTrackingFlowActive = true;
    this.activeTrackingMode = action;
    this.isAwaitingOptionSelection = false;
  }


  resetTrackingFlow(): void {
    this.isTrackingFlowActive = false;
    this.activeTrackingMode = '';
    this.userInput = '';
    this.isAwaitingOptionSelection = false;

    const secondaryActions = this.menuConfig.secondaryMenu[this.activePrimaryMenuKey] || [];

    this.messages.push({
      text: this.secondaryMenuMessage,
      isUser: false,
      timestamp: new Date(),
      type: 'tracking-menu',
      actions: secondaryActions
    });
  }

  getActionIcon(key: string): string {
    return this.actionIconMap[key] || `${this.iconBase}/main_menu.svg`;
  }

  getActionSubtitle(key: string): string {
    return this.actionSubtitleMap[key] || '';
  }

  getActionLabel(action: ChatMenuItem): string {
    return this.actionLabelMap[action?.key] || action?.label || '';
  }

  private getValidation() {
    return getTrackingValidation(
      this.userInput,
      this.isTrackingFlowActive,
      this.currentTrackingMode
    );
  }

  get isTrackingInputReady(): boolean {
    return this.isTrackingFlowActive
      && !!this.currentTrackingMode
      && isValidTrackingMode(this.currentTrackingMode);
  }

  get isInputDisabled(): boolean {
    // Live agent chat is free text, so the tracking-flow gating does not apply.
    if (this.chatMode === 'live') {
      return false;
    }
    return this.isTyping || this.isAwaitingOptionSelection || !this.isTrackingInputReady;
  }

  get isSendDisabled(): boolean {
    if (this.chatMode === 'live') {
      return !this.userInput.trim();
    }
    return this.isInputDisabled || this.getValidation().isSendDisabled;
  }

  private focusMessageInput(): void {
    setTimeout(() => {
      const input = this.messageInput?.nativeElement;
      if (input && !input.disabled) {
        input.focus();
      }
    });
  }

  get validationError(): string {
    return this.getValidation().validationError;
  }

  get inputPlaceholder(): string {
    if (this.isTyping) {
      return 'AI is processing...';
    }
    if (this.isTrackingInputReady && !this.isAwaitingOptionSelection) {
      return TRACKING_PLACEHOLDERS[this.currentTrackingMode] || 'Type your message...';
    }
    return 'Select an option above to continue...';
  }

  getShipmentValidation(): ShipmentValidationResult {
    return this.getValidation().result || { isValid: true };
  }

  private isNoRecordMessage(message: string): boolean {
    const normalized = (message || '').toLowerCase();
    if (normalized === 'no record found') {
      return true;
    }
  }

  getTrackingDisplay(status?: string, estimatedDelivery?: string): {
    className: string;
    label: string;
    sub: string;
    suffix: string;
    isDelivered: boolean;
    icon: string;
  } {
    const normalized = (status || '').toUpperCase();

    if (normalized === 'DELIVERED') {
      return {
        className: 'delivery-status-card delivery-status-card--delivered',
        label: 'DELIVERED',
        sub: 'Your order has been delivered',
        suffix: 'has been delivered.',
        isDelivered: true,
        icon: 'checkmark.svg'
      };
    }
    if (normalized === 'IN-TRANSIT' || normalized === 'Y' || normalized === 'ACTIVE' || normalized === 'SHIPPED IN TRANSIT') {
      return {
        className: 'delivery-status-card delivery-status-card--in-transit',
        label: 'SHIPPED IN-TRANSIT',
        sub: 'Order is in Transit',
        suffix: 'is in transit.',
        isDelivered: false,
        icon: 'checkmark.svg'
      };
    }


    return {
      className: 'delivery-status-card delivery-status-card--in-progress',
      label: 'ORDER IN PROGRESS',
      sub: 'Order in progress',
      suffix: 'is in progress.',
      isDelivered: false,
      icon: 'checkmark.svg'
    };
  }

  onTrackingDetailSelect(trackingData: ChatMessage['trackingData']): void {
    this.selectedTrackingDetail = true;
    const trackingUrl = trackingData?.trackingUrl?.trim();
    if (trackingUrl) {
      window.open(trackingUrl, '_blank');
    }
  }

  trackAnotherOrder(): void {
    this.isTrackingFlowActive = false;
    this.activeTrackingMode = '';
    this.userInput = '';
    this.selectedTrackingDetail = false;
    this.isAwaitingOptionSelection = false;

    const secondaryActions = this.menuConfig.secondaryMenu[this.activePrimaryMenuKey] || [];

    this.messages.push({
      text: this.secondaryMenuMessage,
      isUser: false,
      timestamp: new Date(),
      type: 'tracking-menu',
      actions: secondaryActions
    });
  }
}
